import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, BadgeCheck, BadgeX, Calculator, ChevronLeft, ChevronRight, Eye, EyeOff, FileSpreadsheet, Filter, Info, Loader2, MapPin, Plus, RefreshCw, Search, Trash2, Truck, X } from 'lucide-react';
import * as XLSX from 'xlsx-js-style';
import { toast } from 'sonner';
import { demurrageService, type DemurrageReport, type DemurrageReportRow } from '../services/demurrageService';
import { pointVisitLogService } from '../services/pointVisitLogService';
import { warehouseService } from '../services/warehouseService';

const formatDateTimeTr = (value: string | null | undefined) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const formatDateInput = (value: Date) => `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, '0')}-${String(value.getDate()).padStart(2, '0')}`;

const openMapLocation = (latitude: number | null | undefined, longitude: number | null | undefined, label?: string) => {
  if (latitude == null || longitude == null) return;
  const query = `${Number(latitude)},${Number(longitude)}${label ? ` (${label})` : ''}`;
  window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`, '_blank', 'noopener,noreferrer');
};

const formatMoney = (value: number | string | null | undefined) => {
  const numericValue = Number(value || 0);
  return `${numericValue.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺`;
};

const normalizeSearchText = (value: unknown) => String(value ?? '')
  .toLocaleLowerCase('tr-TR')
  .replace(/ı/g, 'i')
  .replace(/ğ/g, 'g')
  .replace(/ü/g, 'u')
  .replace(/ş/g, 's')
  .replace(/ö/g, 'o')
  .replace(/ç/g, 'c')
  .replace(/\s+/g, ' ')
  .trim();

const makeId = () => `id_${Date.now()}_${Math.random().toString(16).slice(2)}`;

type SortDirection = 'asc' | 'desc';

type FilterOperator = 'contains' | 'notContains' | 'equals' | 'notEquals' | 'empty' | 'notEmpty' | 'dateEquals' | 'dateOnOrAfter' | 'dateOnOrBefore' | 'dateRange' | 'dateToday' | 'dateThisWeek' | 'dateThisMonth' | 'dateLastMonth';
type FilterJoin = 'and' | 'or';

type ColumnKey =
  | 'shipmentNo'
  | 'orderCode'
  | 'planCode'
  | 'transportModel'
  | 'plate'
  | 'driverName'
  | 'pickupDate'
  | 'assignedAt'
  | 'facilityName'
  | 'deliveryName'
  | 'cityDistrict'
  | 'firstArrivalAt'
  | 'lastDepartureAt'
  | 'hasLogWindow'
  | 'arrivalAt'
  | 'unloadingAt'
  | 'eligible'
  | 'reflected'
  | 'nDays'
  | 'totalAmount'
  | 'notes';

type ColumnDef = {
  key: ColumnKey;
  label: string;
  width: number;
  align?: 'left' | 'right';
  isDate?: boolean;
};

type FilterRule = {
  id: string;
  join: FilterJoin;
  column: ColumnKey;
  operator: FilterOperator;
  value: string;
};

const REPORT_COLUMNS: ColumnDef[] = [
  { key: 'shipmentNo', label: 'Sevkiyat No', width: 150 },
  { key: 'orderCode', label: 'Sipariş Kodu', width: 150 },
  { key: 'planCode', label: 'Plan Kodu', width: 150 },
  { key: 'transportModel', label: 'Araç Tipi', width: 120 },
  { key: 'plate', label: 'Plaka', width: 130 },
  { key: 'driverName', label: 'Sürücü', width: 190 },
  { key: 'pickupDate', label: 'Yükleme Talep', width: 170, isDate: true },
  { key: 'assignedAt', label: 'Araç Atama', width: 170, isDate: true },
  { key: 'facilityName', label: 'Yükleme Noktaları', width: 280 },
  { key: 'deliveryName', label: 'Teslim Noktaları', width: 280 },
  { key: 'cityDistrict', label: 'İl / İlçe', width: 160 },
  { key: 'firstArrivalAt', label: 'İlk Nokta Varış', width: 170, isDate: true },
  { key: 'lastDepartureAt', label: 'Son Nokta Ayrılış', width: 170, isDate: true },
  { key: 'hasLogWindow', label: 'Log Durumu', width: 160 },
  { key: 'arrivalAt', label: 'Hesap Varışı', width: 170, isDate: true },
  { key: 'unloadingAt', label: 'Hesap Bitişi', width: 170, isDate: true },
  { key: 'eligible', label: 'Hakediş', width: 130 },
  { key: 'reflected', label: 'Yansıtıldı', width: 130 },
  { key: 'nDays', label: 'Gün', width: 90, align: 'right' },
  { key: 'totalAmount', label: 'Tutar', width: 150, align: 'right' },
  { key: 'notes', label: 'Açıklama', width: 280 },
];

const DEFAULT_VISIBLE_COLUMNS: ColumnKey[] = [
  'shipmentNo',
  'orderCode',
  'planCode',
  'transportModel',
  'plate',
  'driverName',
  'pickupDate',
  'facilityName',
  'deliveryName',
  'firstArrivalAt',
  'lastDepartureAt',
  'hasLogWindow',
  'eligible',
  'reflected',
  'nDays',
  'totalAmount',
  'notes',
];

const DATE_FILTER_COLUMNS = new Set<ColumnKey>(REPORT_COLUMNS.filter((column) => column.isDate).map((column) => column.key));

const getColumn = (key: ColumnKey) => REPORT_COLUMNS.find((column) => column.key === key);

const transportModelLabel = (value: string) => (value === 'own-fleet' ? 'Özmal' : value === 'spot' ? 'Spot' : (value || '-'));

const getDateFilterComparableValue = (value: string | null | undefined) => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;
  const trMatch = raw.match(/^(\d{2})\.(\d{2})\.(\d{4})/);
  if (trMatch) return `${trMatch[3]}-${trMatch[2]}-${trMatch[1]}`;
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return '';
  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const day = String(parsed.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getDatePresetRange = (operator: FilterOperator) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const format = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  if (operator === 'dateToday') {
    const value = format(today);
    return { start: value, end: value };
  }
  if (operator === 'dateThisWeek') {
    const dayOfWeek = today.getDay();
    const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    const start = new Date(today);
    start.setDate(today.getDate() + mondayOffset);
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    return { start: format(start), end: format(end) };
  }
  if (operator === 'dateThisMonth') {
    const start = new Date(today.getFullYear(), today.getMonth(), 1);
    const end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    return { start: format(start), end: format(end) };
  }
  if (operator === 'dateLastMonth') {
    const start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const end = new Date(today.getFullYear(), today.getMonth(), 0);
    return { start: format(start), end: format(end) };
  }
  return null;
};

const getComparableValue = (row: DemurrageReportRow, key: ColumnKey) => {
  if (DATE_FILTER_COLUMNS.has(key)) return formatDateTimeTr(row[key] as string | null | undefined);
  switch (key) {
    case 'transportModel':
      return transportModelLabel(row.transportModel);
    case 'cityDistrict':
      return `${row.city} / ${row.district}`;
    case 'hasLogWindow':
      return row.hasLogWindow ? 'Log Var' : 'Logu Yok';
    case 'eligible':
      return row.eligible ? 'Hakediyor' : 'Haketmiyor';
    case 'reflected':
      return row.reflected ? 'Yansıtıldı' : 'Yansıtılmadı';
    case 'nDays':
      return String(row.nDays ?? '');
    case 'totalAmount':
      return String(row.totalAmount ?? '');
    default:
      return String(row[key as keyof DemurrageReportRow] ?? '');
  }
};

const evaluateFilter = (row: DemurrageReportRow, rule: FilterRule) => {
  if (DATE_FILTER_COLUMNS.has(rule.column)) {
    const cellDate = getDateFilterComparableValue(row[rule.column] as string | null | undefined);
    const ruleDate = getDateFilterComparableValue(rule.value);

    switch (rule.operator) {
      case 'dateEquals':
        return !ruleDate || !cellDate ? true : cellDate === ruleDate;
      case 'dateOnOrAfter':
        return !ruleDate || !cellDate ? true : cellDate >= ruleDate;
      case 'dateOnOrBefore':
        return !ruleDate || !cellDate ? true : cellDate <= ruleDate;
      case 'dateRange': {
        const [startRaw, endRaw] = String(rule.value).split(',');
        const start = getDateFilterComparableValue(startRaw);
        const end = getDateFilterComparableValue(endRaw);
        if (!start && !end) return true;
        if (!cellDate) return true;
        if (start && end) return cellDate >= start && cellDate <= end;
        return start ? cellDate >= start : cellDate <= end;
      }
      case 'dateToday':
      case 'dateThisWeek':
      case 'dateThisMonth':
      case 'dateLastMonth': {
        const range = getDatePresetRange(rule.operator);
        return !range || !cellDate ? true : cellDate >= range.start && cellDate <= range.end;
      }
      default:
        break;
    }
  }

  const cell = normalizeSearchText(getComparableValue(row, rule.column));
  const value = normalizeSearchText(rule.value);

  switch (rule.operator) {
    case 'contains':
      return value ? cell.includes(value) : true;
    case 'notContains':
      return value ? !cell.includes(value) : true;
    case 'equals':
      return value ? cell === value : true;
    case 'notEquals':
      return value ? cell !== value : true;
    case 'empty':
      return cell === '' || cell === '-';
    case 'notEmpty':
      return cell !== '' && cell !== '-';
    default:
      return true;
  }
};

const EMPTY_REPORT: DemurrageReport = {
  settings: { transitionDate: '', dailyFee: 0, geofenceKm: 2, customerCutoffTime: '13:00', depotCutoffTime: '13:00', active: false },
  summary: { shipmentCount: 0, eligibleCount: 0, ineligibleCount: 0, totalDays: 0, totalAmount: 0 },
  details: [],
  byFacility: [],
  byCity: [],
  byDistrict: [],
};

export function BeklemeBedeliRaporu() {
  const today = useMemo(() => {
    const date = new Date();
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  }, []);
  const monthStart = useMemo(() => `${today.slice(0, 8)}01`, [today]);

  const [from, setFrom] = useState(monthStart);
  const [to, setTo] = useState(today);
  const [eventFrom, setEventFrom] = useState('');
  const [eventTo, setEventTo] = useState('');
  const [warehouseId, setWarehouseId] = useState('');
  const [plate, setPlate] = useState('');
  const [eligibilityFilter, setEligibilityFilter] = useState<'all' | 'eligible' | 'ineligible'>('all');
  const [reasonFilter, setReasonFilter] = useState('all');
  const [warehouses, setWarehouses] = useState<Array<{ id: string | number; name: string }>>([]);
  const [report, setReport] = useState<DemurrageReport>(EMPTY_REPORT);
  const [loading, setLoading] = useState(false);
  const [backfillingOwnFleet, setBackfillingOwnFleet] = useState(false);
  const [bulkRecalculationProgress, setBulkRecalculationProgress] = useState<{
    total: number;
    completed: number;
    succeeded: number;
    failed: number;
    currentShipmentNo: string;
    currentTransportModel: string;
  } | null>(null);
  const [applyingDemurrage, setApplyingDemurrage] = useState(false);
  const [correctingDemurrage, setCorrectingDemurrage] = useState(false);
  const [selectedShipmentIds, setSelectedShipmentIds] = useState<string[]>([]);
  const [recalculatingShipmentId, setRecalculatingShipmentId] = useState<string | null>(null);
  const [acceptingGeofenceKey, setAcceptingGeofenceKey] = useState<string | null>(null);
  const [verifyingShipmentId, setVerifyingShipmentId] = useState<string | null>(null);
  const [verifyData, setVerifyData] = useState<any>(null);
  const [verifyExpandedPoints, setVerifyExpandedPoints] = useState<Record<string, boolean>>({});
  const [recalculationDetails, setRecalculationDetails] = useState<Record<string, {
    locationCount: number;
    logsWritten: number;
    pointDebug: Array<{
      pointKey: string;
      pointType: string;
      pointSource: string | null;
      pointName: string;
      latitude: number | null;
      longitude: number | null;
      geofenceKm: number;
      overrideGeofenceKm: number | null;
      shipmentDeliveryLatitude: number | null;
      shipmentDeliveryLongitude: number | null;
      deliveryCoordinateDiffKm: number | null;
      nearestLocation: {
        dataTime: string | null;
        plate: string | null;
        latitude: number | null;
        longitude: number | null;
        distanceKm: number;
      } | null;
    }>;
    dayStats: Array<{ date: string; startDateTime: string; endDateTime: string; locationCount: number; arrivalsWritten: number; departuresWritten: number }>;
  }>>({});
  const [error, setError] = useState('');

  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<FilterRule[]>([]);
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [showColumnPanel, setShowColumnPanel] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState<ColumnKey[]>(DEFAULT_VISIBLE_COLUMNS);
  const [sortKey, setSortKey] = useState<ColumnKey>('pickupDate');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  useEffect(() => {
    warehouseService.getWarehouses()
      .then((response: any) => {
        const list = Array.isArray(response?.data) ? response.data : [];
        setWarehouses(list.map((item: any) => ({ id: item.id ?? item._id, name: item.name || item.company_name || '-' })));
      })
      .catch(() => {});
  }, []);

  const loadReport = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await demurrageService.getReport({
        from,
        to,
        warehouseId: warehouseId || undefined,
        plate: plate.trim() || undefined,
        eventFrom: eventFrom || undefined,
        eventTo: eventTo || undefined,
      });
      setReport(response?.data || EMPTY_REPORT);
    } catch (err: any) {
      console.error('Bekleme bedeli raporu yüklenemedi:', err);
      setError(err?.message || 'Rapor oluşturulurken bir hata oluştu');
      setReport(EMPTY_REPORT);
    } finally {
      setLoading(false);
    }
  }, [from, to, warehouseId, plate, eventFrom, eventTo]);

  useEffect(() => {
    void loadReport();
  }, [loadReport]);

  const reasonScopedRows = useMemo(() => {
    let rows = report.details;
    if (eligibilityFilter === 'eligible') rows = rows.filter((row) => row.eligible);
    if (eligibilityFilter === 'ineligible') rows = rows.filter((row) => !row.eligible);
    if (reasonFilter !== 'all') rows = rows.filter((row) => (row.notes || '-') === reasonFilter);
    return rows;
  }, [report.details, reasonFilter, eligibilityFilter]);

  const filteredRows = useMemo(() => {
    let rows = reasonScopedRows;
    const q = normalizeSearchText(search);

    if (q) {
      rows = rows.filter((row) => {
        const displayedValues = REPORT_COLUMNS.map((column) => getComparableValue(row, column.key));
        const rawValues = Object.values(row).map((value) => String(value ?? ''));
        return normalizeSearchText([...displayedValues, ...rawValues].join(' ')).includes(q);
      });
    }

    const activeFilters = filters.filter((rule) => ['empty', 'notEmpty', 'dateToday', 'dateThisWeek', 'dateThisMonth', 'dateLastMonth'].includes(rule.operator) || normalizeSearchText(rule.value));

    if (activeFilters.length > 0) {
      rows = rows.filter((row) => {
        let accepted = evaluateFilter(row, activeFilters[0]);
        for (let index = 1; index < activeFilters.length; index += 1) {
          const rule = activeFilters[index];
          const next = evaluateFilter(row, rule);
          accepted = rule.join === 'or' ? accepted || next : accepted && next;
        }
        return accepted;
      });
    }

    return rows;
  }, [reasonScopedRows, search, filters]);

  const sortedRows = useMemo(() => {
    const getValue = (row: DemurrageReportRow, key: ColumnKey) => {
      if (DATE_FILTER_COLUMNS.has(key)) return row[key] ? new Date(row[key] as string).getTime() : 0;
      switch (key) {
        case 'eligible':
          return row.eligible ? 1 : 0;
        case 'reflected':
          return row.reflected ? 1 : 0;
        case 'hasLogWindow':
          return row.hasLogWindow ? 1 : 0;
        case 'nDays':
          return Number(row.nDays || 0);
        case 'totalAmount':
          return Number(row.totalAmount || 0);
        case 'cityDistrict':
          return `${row.city} / ${row.district}`.toLocaleLowerCase('tr-TR');
        default:
          return String(row[key as keyof DemurrageReportRow] || '').toLocaleLowerCase('tr-TR');
      }
    };
    return [...filteredRows].sort((a, b) => {
      const left = getValue(a, sortKey);
      const right = getValue(b, sortKey);
      if (left < right) return sortDirection === 'asc' ? -1 : 1;
      if (left > right) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredRows, sortKey, sortDirection]);

  const filteredSummary = useMemo(() => ({
    shipmentCount: sortedRows.length,
    eligibleCount: sortedRows.filter((row) => row.eligible).length,
    ineligibleCount: sortedRows.filter((row) => !row.eligible).length,
    totalAmount: sortedRows.reduce((sum, row) => sum + Number(row.totalAmount || 0), 0),
  }), [sortedRows]);

  const toggleSort = useCallback((key: ColumnKey) => {
    if (sortKey === key) {
      setSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortKey(key);
    setSortDirection(key === 'pickupDate' ? 'desc' : 'asc');
  }, [sortKey]);

  const activeColumns = useMemo(() => {
    const fixed = REPORT_COLUMNS.filter((column) => visibleColumns.includes(column.key));
    const defaults = REPORT_COLUMNS.filter((column) => !visibleColumns.includes(column.key));
    return [...fixed, ...defaults];
  }, [visibleColumns]);

  const totalPages = Math.max(1, Math.ceil(sortedRows.length / pageSize));
  const clampedPage = Math.min(page, totalPages);
  const paginatedRows = sortedRows.slice((clampedPage - 1) * pageSize, clampedPage * pageSize);
  const allVisibleSelected = paginatedRows.length > 0 && paginatedRows.every((row) => selectedShipmentIds.includes(String(row.shipmentId)));

  useEffect(() => {
    setPage(1);
  }, [search, filters, visibleColumns, pageSize, sortKey, sortDirection, reasonFilter, eligibilityFilter]);

  const activeFilterCount = filters.filter((rule) => ['empty', 'notEmpty', 'dateToday', 'dateThisWeek', 'dateThisMonth', 'dateLastMonth'].includes(rule.operator) || normalizeSearchText(rule.value)).length + (search ? 1 : 0);

  const addFilterRule = () => {
    setFilters((current) => [...current, { id: makeId(), join: 'and', column: 'shipmentNo', operator: 'contains', value: '' }]);
  };

  const updateFilterRule = (id: string, patch: Partial<FilterRule>) => {
    setFilters((current) => current.map((rule) => (rule.id === id ? { ...rule, ...patch } : rule)));
  };

  const removeFilterRule = (id: string) => {
    setFilters((current) => current.filter((rule) => rule.id !== id));
  };

  const clearFilters = () => {
    setSearch('');
    setFilters([]);
    setEligibilityFilter('all');
    setReasonFilter('all');
    setVisibleColumns(DEFAULT_VISIBLE_COLUMNS);
    setSortKey('pickupDate');
    setSortDirection('desc');
    setPage(1);
  };

  const toggleVisibleRows = () => {
    const visibleIds = paginatedRows.map((row) => String(row.shipmentId));
    setSelectedShipmentIds((current) => allVisibleSelected
      ? current.filter((id) => !visibleIds.includes(id))
      : Array.from(new Set([...current, ...visibleIds])));
  };

  const toggleShipmentSelection = (shipmentId: string) => {
    setSelectedShipmentIds((current) => current.includes(shipmentId)
      ? current.filter((id) => id !== shipmentId)
      : [...current, shipmentId]);
  };

  const renderSortHeader = (label: string, key: ColumnKey, align: 'left' | 'right' = 'left') => (
    <button
      type="button"
      onClick={() => toggleSort(key)}
      className={`flex w-full items-center gap-1 ${align === 'right' ? 'justify-end text-right' : 'justify-start text-left'} hover:text-slate-900 dark:hover:text-slate-100`}
      title="Sırala"
    >
      <span>{label}</span>
      {sortKey === key ? (
        sortDirection === 'asc' ? <ArrowUp className="h-3.5 w-3.5 shrink-0 text-blue-600" /> : <ArrowDown className="h-3.5 w-3.5 shrink-0 text-blue-600" />
      ) : (
        <span className="text-[10px] text-slate-400">--</span>
      )}
    </button>
  );

  const renderCell = (row: DemurrageReportRow, column: ColumnDef) => {
    const alignClass = column.align === 'right' ? 'text-right' : 'text-left';
    switch (column.key) {
      case 'transportModel':
        return (
          <span className={`inline-block whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-semibold ${row.transportModel === 'own-fleet' ? 'bg-orange-100 text-orange-800 dark:bg-orange-950 dark:text-orange-300' : row.transportModel === 'spot' ? 'bg-violet-100 text-violet-800 dark:bg-violet-950 dark:text-violet-300' : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300'}`}>
            {transportModelLabel(row.transportModel)}
          </span>
        );
      case 'cityDistrict':
        return <span className="whitespace-nowrap">{row.city} / {row.district}</span>;
      case 'hasLogWindow':
        return (
          <span className={`inline-block whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-semibold ${row.hasLogWindow ? 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300' : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300'}`}>
            {row.hasLogWindow ? 'Log Var' : 'Logu Yok'}
          </span>
        );
      case 'eligible':
        return (
          <span className={`inline-block whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-semibold ${row.eligible ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300' : 'bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-300'}`}>
            {row.eligible ? 'Hakediyor' : 'Haketmiyor'}
          </span>
        );
      case 'reflected':
        return (
          <span className={`inline-block whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-semibold ${row.reflected ? 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300' : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300'}`}>
            {row.reflected ? 'Yansıtıldı' : 'Yansıtılmadı'}
          </span>
        );
      case 'nDays':
        return <span className="whitespace-nowrap font-medium text-slate-800 dark:text-slate-100">{row.nDays}</span>;
      case 'totalAmount':
        return <span className="whitespace-nowrap font-semibold text-slate-800 dark:text-slate-100">{formatMoney(row.totalAmount)}</span>;
      case 'shipmentNo':
        return <span className="font-medium text-indigo-700 dark:text-indigo-300">{row.shipmentNo}</span>;
      default:
        if (DATE_FILTER_COLUMNS.has(column.key)) {
          return <span className="whitespace-nowrap">{formatDateTimeTr(row[column.key] as string | null | undefined)}</span>;
        }
        return <span className="whitespace-nowrap">{row[column.key] || '-'}</span>;
    }
  };

  const reasonTabs = useMemo(() => {
    const reasons = Array.from(new Set(
      report.details
        .filter((row) => !row.eligible)
        .map((row) => String(row.notes || '-').trim() || '-')
    ));
    return ['all', ...reasons];
  }, [report.details]);

  const applyShipmentRecalculationDetail = useCallback((shipmentId: string, shipmentResult?: any) => {
    if (!shipmentResult) return;
    setRecalculationDetails((current) => ({
      ...current,
      [String(shipmentId)]: {
        locationCount: shipmentResult.locationCount,
        logsWritten: shipmentResult.logsWritten,
        pointDebug: shipmentResult.pointDebug || [],
        dayStats: shipmentResult.dayStats || [],
      },
    }));
  }, []);

  const runShipmentRecalculation = useCallback(async (
    shipmentId: string,
    transportModel: string,
    options: { notify?: boolean; reloadAfter?: boolean } = {},
  ) => {
    const { notify = true, reloadAfter = true } = options;
    setRecalculatingShipmentId(shipmentId);
    try {
      const response = transportModel === 'spot'
        ? await pointVisitLogService.backfillSpotDrivers({ shipmentId })
        : await pointVisitLogService.backfillOwnFleet({ shipmentId });
      const result = response?.data;
      const shipmentResult = result?.shipmentResults?.find((item: any) => String(item.shipmentId) === String(shipmentId));
      applyShipmentRecalculationDetail(shipmentId, shipmentResult);

      if (notify) {
        if (transportModel === 'spot') {
          toast.success(result
            ? `${shipmentId} için spot sürücü yeniden hesaplaması tamamlandı. Driver locations'dan ${shipmentResult?.locationCount ?? 0} konum bulundu, ${shipmentResult?.logsWritten ?? result.logged ?? 0} log yazıldı.`
            : 'Spot sürücü yeniden hesaplaması tamamlandı');
        } else {
          toast.success(result
            ? `${shipmentId} için yeniden hesaplama tamamlandı. ${shipmentResult?.matchedPlate ? `Plaka: ${shipmentResult.matchedPlate}. ` : ''}Seyir'den ${shipmentResult?.locationCount ?? result.totalLocationLogs ?? 0} konum bulundu, ${shipmentResult?.logsWritten ?? result.logged ?? 0} log yazıldı.`
            : 'Yeniden hesaplama tamamlandı');
        }
      }

      if (reloadAfter) {
        await loadReport();
      }

      return true;
    } catch (error: any) {
      if (transportModel === 'spot') {
        console.error('Spot shipment recalc failed:', error);
        if (notify) toast.error(error?.message || 'Spot sürücü yeniden hesaplaması yapılamadı');
      } else {
        console.error('Shipment own fleet recalc failed:', error);
        if (notify) toast.error(error?.message || 'Yeniden hesaplama yapılamadı');
      }
      return false;
    } finally {
      setRecalculatingShipmentId(null);
    }
  }, [applyShipmentRecalculationDetail, loadReport]);

  const handleOwnFleetBackfill = useCallback(async () => {
    const recalculationRows = Array.from(
      new Map(
        report.details
          .filter((row) => ['own-fleet', 'spot'].includes(row.transportModel))
          .map((row) => [String(row.shipmentId), row]),
      ).values(),
    );

    if (recalculationRows.length === 0) {
      toast.error('Tarih aralığında yeniden hesaplanacak özmal veya spot kayıt bulunamadı');
      return;
    }

    try {
      setBackfillingOwnFleet(true);
      setBulkRecalculationProgress({
        total: recalculationRows.length,
        completed: 0,
        succeeded: 0,
        failed: 0,
        currentShipmentNo: recalculationRows[0].shipmentNo || '-',
        currentTransportModel: recalculationRows[0].transportModel,
      });

      let succeeded = 0;
      let failed = 0;
      for (let index = 0; index < recalculationRows.length; index += 1) {
        const row = recalculationRows[index];
        const shipmentId = String(row.shipmentId);
        setRecalculatingShipmentId(shipmentId);
        setBulkRecalculationProgress((current) => current ? {
          ...current,
          currentShipmentNo: row.shipmentNo || shipmentId,
          currentTransportModel: row.transportModel,
        } : current);

        try {
          const ok = await runShipmentRecalculation(shipmentId, row.transportModel, { notify: false, reloadAfter: false });
          if (ok) succeeded += 1;
          else failed += 1;
        } catch (error) {
          failed += 1;
          console.error(`Bulk shipment recalculation failed for ${shipmentId}:`, error);
        } finally {
          setBulkRecalculationProgress((current) => current ? {
            ...current,
            completed: index + 1,
            succeeded,
            failed,
          } : current);
        }
      }

      setRecalculatingShipmentId(null);
      toast.success(`${recalculationRows.length} kayıt yeniden hesaplandı. Başarılı: ${succeeded}, Hatalı: ${failed}`);
      await loadReport();
    } catch (error: any) {
      console.error('Bulk shipment recalculation failed:', error);
      toast.error(error?.message || 'Kayıtlar yeniden hesaplanamadı');
    } finally {
      setRecalculatingShipmentId(null);
      setBackfillingOwnFleet(false);
    }
  }, [loadReport, report.details, runShipmentRecalculation]);

  const handleShipmentRecalculate = useCallback(async (shipmentId: string) => {
    await runShipmentRecalculation(shipmentId, 'own-fleet', { notify: true, reloadAfter: true });
  }, [runShipmentRecalculation]);

  const handleSpotShipmentRecalculate = useCallback(async (shipmentId: string) => {
    await runShipmentRecalculation(shipmentId, 'spot', { notify: true, reloadAfter: true });
  }, [runShipmentRecalculation]);

  const handleAcceptNearestGeofence = useCallback(async (shipmentId: string, point: {
    pointKey: string;
    pointName: string;
    nearestLocation: { distanceKm: number } | null;
  }) => {
    if (!point.nearestLocation?.distanceKm) {
      toast.error('Kabul edilecek en yakın mesafe bulunamadı');
      return;
    }
    const actionKey = `${shipmentId}:${point.pointKey}`;
    try {
      setAcceptingGeofenceKey(actionKey);
      const response = await pointVisitLogService.acceptNearestGeofence({
        shipmentId,
        pointKey: point.pointKey,
        geofenceKm: point.nearestLocation.distanceKm,
      });
      const result = response?.data;
      const shipmentResult = result?.shipmentResults?.find((item) => String(item.shipmentId) === String(shipmentId));
      applyShipmentRecalculationDetail(shipmentId, shipmentResult);
      toast.success(`${point.pointName} için ${point.nearestLocation.distanceKm} km geofence kabul edildi ve yeniden hesaplandı.`);
      await loadReport();
    } catch (err: any) {
      console.error('Accept nearest geofence failed:', err);
      toast.error(err?.message || 'Geofence kabul işlemi yapılamadı');
    } finally {
      setAcceptingGeofenceKey(null);
    }
  }, [applyShipmentRecalculationDetail, loadReport]);

  const handleVerifyShipment = useCallback(async (shipmentId: string) => {
    try {
      setVerifyingShipmentId(shipmentId);
      setVerifyData(null);
      const response = await pointVisitLogService.verifyShipment(shipmentId);
      setVerifyData(response?.data || response);
    } catch (err: any) {
      console.error('Verify shipment failed:', err);
      toast.error(err?.message || 'Sevkiyat doğrulama yapılamadı');
    } finally {
      setVerifyingShipmentId(null);
    }
  }, []);

  const handleSpotLocationCheck = useCallback((row: DemurrageReportRow) => {
    const pickupDate = new Date(row.pickupDate || '');
    if (Number.isNaN(pickupDate.getTime())) {
      toast.error('Bu sevkiyat için geçerli yükleme talep tarihi bulunamadı');
      return;
    }
    pickupDate.setHours(0, 0, 0, 0);
    const endDate = new Date(pickupDate);
    endDate.setDate(endDate.getDate() + 2);
    const params = new URLSearchParams({
      plate: String(row.plate || '').trim(),
      from: formatDateInput(pickupDate),
      to: formatDateInput(endDate),
    });
    window.location.href = `/spot-driver-location-logs?${params.toString()}`;
  }, []);

  const handleApplyDemurrage = useCallback(async () => {
    try {
      setApplyingDemurrage(true);
      const shipmentIds = sortedRows
        .filter((row) => selectedShipmentIds.includes(String(row.shipmentId)) && row.eligible && !row.reflected)
        .map((row) => String(row.shipmentId || '').trim())
        .filter((value) => value.length > 0);
      if (shipmentIds.length === 0) {
        toast.error('Seçili satırlarda yansıtılacak uygun hakediş bulunamadı');
        return;
      }
      const response: any = await demurrageService.applyToShipments(shipmentIds);
      toast.success(response?.message || 'Bekleme bedelleri sevkiyatlara yansıtıldı');
      setSelectedShipmentIds([]);
      await loadReport();
    } catch (error: any) {
      console.error('Apply demurrage failed:', error);
      toast.error(error?.message || 'Bekleme bedeli yansıtılamadı');
    } finally {
      setApplyingDemurrage(false);
    }
  }, [loadReport, selectedShipmentIds, sortedRows]);

  const handleCorrectDemurrage = useCallback(async () => {
    try {
      setCorrectingDemurrage(true);
      const shipmentIds = sortedRows
        .filter((row) => selectedShipmentIds.includes(String(row.shipmentId)))
        .map((row) => String(row.shipmentId || '').trim())
        .filter((value) => value.length > 0);
      if (shipmentIds.length === 0) {
        toast.error('Düzeltilecek sevkiyat seçilmedi');
        return;
      }
      const response: any = await demurrageService.correctToShipments(shipmentIds);
      toast.success(response?.message || 'Yatışlar düzeltildi');
      setSelectedShipmentIds([]);
      await loadReport();
    } catch (error: any) {
      console.error('Correct demurrage failed:', error);
      toast.error(error?.message || 'Yatış düzeltilemedi');
    } finally {
      setCorrectingDemurrage(false);
    }
  }, [loadReport, selectedShipmentIds, sortedRows]);

  const exportExcel = () => {
    const workbook = XLSX.utils.book_new();
    const sheet = XLSX.utils.json_to_sheet(sortedRows.map((row) => ({
      'Sevkiyat No': row.shipmentNo,
      'Sipariş Kodu': row.orderCode || '-',
      'Plan Kodu': row.planCode || '-',
      'Araç Tipi': transportModelLabel(row.transportModel),
      'Plaka': row.plate,
      'Sürücü': row.driverName,
      'Yükleme Talep Tarihi': formatDateTimeTr(row.pickupDate),
      'Araç Atama Tarihi': formatDateTimeTr(row.assignedAt),
      'Yükleme Noktaları': row.facilityName,
      'Teslim Noktaları': row.deliveryName,
      'İl / İlçe': `${row.city} / ${row.district}`,
      'İlk Nokta Varış': formatDateTimeTr(row.firstArrivalAt),
      'Son Nokta Ayrılış': formatDateTimeTr(row.lastDepartureAt),
      'Log Durumu': row.hasLogWindow ? 'Log Var' : 'Logu Yok',
      'Hesap Varışı': formatDateTimeTr(row.arrivalAt),
      'Hesap Bitişi': formatDateTimeTr(row.unloadingAt),
      'Hakediş': row.eligible ? 'Hakediyor' : 'Haketmiyor',
      'Yatma Günü': row.nDays,
      'Bekleme Bedeli': row.totalAmount,
      'Açıklama': row.notes,
    })));
    sheet['!cols'] = [{ wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 12 }, { wch: 14 }, { wch: 22 }, { wch: 18 }, { wch: 18 }, { wch: 24 }, { wch: 26 }, { wch: 18 }, { wch: 18 }, { wch: 18 }, { wch: 20 }, { wch: 12 }, { wch: 10 }, { wch: 14 }, { wch: 32 }];
    XLSX.utils.book_append_sheet(workbook, sheet, 'Bekleme Bedeli');
    XLSX.writeFile(workbook, `bekleme-bedeli-raporu-${from}-${to}.xlsx`);
  };

  const summaryCards = [
    { label: 'Toplam Sevkiyat', value: String(filteredSummary.shipmentCount), icon: Truck, color: 'bg-blue-50 text-blue-700' },
    { label: 'Hakeden', value: String(filteredSummary.eligibleCount), icon: BadgeCheck, color: 'bg-emerald-50 text-emerald-700' },
    { label: 'Hak Etmeyen', value: String(filteredSummary.ineligibleCount), icon: BadgeX, color: 'bg-rose-50 text-rose-700' },
    { label: 'Toplam Bekleme Bedeli', value: formatMoney(filteredSummary.totalAmount), icon: Calculator, color: 'bg-amber-50 text-amber-700' },
  ];

  return (
    <>
    <div className="space-y-6 p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-slate-100">Bekleme Bedeli Raporu</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-slate-400">
            Boşaltma noktasına varış ve boşaltmanın tamamlandığı zamana göre özmal ve spot sevkiyatların bekleme bedeli hakediş raporu.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => void handleApplyDemurrage()}
            disabled={loading || applyingDemurrage || selectedShipmentIds.length === 0}
            className="inline-flex items-center gap-2 rounded-xl border border-blue-300 bg-blue-50 px-3 py-2 text-sm font-medium text-blue-700 hover:bg-blue-100 disabled:opacity-50 dark:border-blue-800 dark:bg-blue-950/50 dark:text-blue-300"
          >
            {applyingDemurrage ? <Loader2 className="h-4 w-4 animate-spin" /> : <BadgeCheck className="h-4 w-4" />}
            Bekleme Bedeline Yansıt
          </button>
          <button
            type="button"
            onClick={() => void handleCorrectDemurrage()}
            disabled={loading || correctingDemurrage || selectedShipmentIds.length === 0}
            className="inline-flex items-center gap-2 rounded-xl border border-violet-300 bg-violet-50 px-3 py-2 text-sm font-medium text-violet-700 hover:bg-violet-100 disabled:opacity-50 dark:border-violet-800 dark:bg-violet-950/50 dark:text-violet-300"
          >
            {correctingDemurrage ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            Yatış Düzelt
          </button>
          <button
            type="button"
            onClick={() => void handleOwnFleetBackfill()}
            disabled={loading || backfillingOwnFleet}
            className="inline-flex items-center gap-2 rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-700 hover:bg-amber-100 disabled:opacity-50 dark:border-amber-800 dark:bg-amber-950/50 dark:text-amber-300"
          >
            {backfillingOwnFleet ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            Tarih Aralığındaki Kayıtları Yeniden Hesapla
          </button>
          <button
            type="button"
            onClick={exportExcel}
            disabled={loading || sortedRows.length === 0}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
          >
            <FileSpreadsheet className="h-4 w-4" />
            Excele Aktar
          </button>
        </div>
      </div>

      {bulkRecalculationProgress && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 dark:border-amber-900/70 dark:bg-amber-950/30">
          <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
            <div className="font-semibold text-amber-900 dark:text-amber-200">
              Yeniden hesaplama ilerlemesi
            </div>
            <div className="text-amber-800 dark:text-amber-300">
              {bulkRecalculationProgress.completed} / {bulkRecalculationProgress.total} tamamlandı
              {' '}({bulkRecalculationProgress.succeeded} başarılı, {bulkRecalculationProgress.failed} hatalı, {Math.max(0, bulkRecalculationProgress.total - bulkRecalculationProgress.completed)} kaldı)
            </div>
          </div>
          <div className="mt-2 h-2 overflow-hidden rounded-full bg-amber-200 dark:bg-amber-900/60">
            <div
              className="h-full rounded-full bg-amber-600 transition-all duration-300 dark:bg-amber-400"
              style={{ width: `${bulkRecalculationProgress.total > 0 ? (bulkRecalculationProgress.completed / bulkRecalculationProgress.total) * 100 : 0}%` }}
            />
          </div>
          <div className="mt-2 text-xs text-amber-800 dark:text-amber-300">
            {bulkRecalculationProgress.completed < bulkRecalculationProgress.total
              ? `İşleniyor: ${bulkRecalculationProgress.currentShipmentNo} (${bulkRecalculationProgress.currentTransportModel === 'spot' ? 'Spot' : 'Özmal'})`
              : 'Tarih aralığındaki tüm kayıtların yeniden hesaplaması tamamlandı.'}
          </div>
        </div>
      )}

      <div className="rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-[#141414]">
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-6">
          <div>
            <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-400">Başlangıç Tarihi</label>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-800 outline-none focus:border-blue-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200" />
          </div>
          <div>
            <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-400">Bitiş Tarihi</label>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-800 outline-none focus:border-blue-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200" />
          </div>
          <div>
            <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-400">Tesis</label>
            <select value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)} className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-800 outline-none focus:border-blue-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200">
              <option value="">Tümü</option>
              {warehouses.map((warehouse) => (
                <option key={String(warehouse.id)} value={String(warehouse.id)}>{warehouse.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-400">Plaka</label>
            <input type="text" value={plate} onChange={(e) => setPlate(e.target.value)} placeholder="Örn: 34 ABC 123" className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-800 outline-none focus:border-blue-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200" />
          </div>
          <div>
            <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-400">İlk Varış Başlangıç</label>
            <input type="datetime-local" value={eventFrom} onChange={(e) => setEventFrom(e.target.value)} className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-800 outline-none focus:border-blue-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200" />
          </div>
          <div>
            <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-400">Son Ayrılış Bitiş</label>
            <input type="datetime-local" value={eventTo} onChange={(e) => setEventTo(e.target.value)} className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-800 outline-none focus:border-blue-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200" />
          </div>
        </div>

        <div className="mt-3 flex flex-wrap items-center justify-end gap-2">
          <button
            type="button"
            onClick={clearFilters}
            className="inline-flex h-10 items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            <X className="h-4 w-4" />
            Filtreleri Temizle
          </button>
          <button type="button" onClick={() => void loadReport()} disabled={loading} className="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Calculator className="h-4 w-4" />}
            Rapor Oluştur
          </button>
        </div>

        <div className="mt-3 flex items-start gap-2 rounded-lg bg-blue-50 p-3 text-xs text-blue-800 dark:bg-blue-950/50 dark:text-blue-300">
          <Info className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            Üstteki başlangıç ve bitiş tarihi filtreleri <strong>yükleme talep tarihi</strong>ne göre çalışır.
            Araç boşaltma noktasının <strong>{report.settings.geofenceKm} km</strong> alanına girdiğinde varış kabul edilir.
            İlk gün için müşteri kesim saati <strong>{report.settings.customerCutoffTime}</strong>, depo kesim saati <strong>{report.settings.depotCutoffTime}</strong>.
            Günlük bekleme bedeli: <strong>{formatMoney(report.settings.dailyFee)}</strong>.
            Bu rapor artık <strong>özmal ve spot</strong> sevkiyatları içerir. Saat filtresi <strong>ilk boşaltma varışı</strong> ve <strong>son boşaltma ayrılışı</strong> alanlarına göre çalışır.
          </div>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {summaryCards.map((card) => (
          <div key={card.label} className="flex items-center gap-4 rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-[#141414]">
            <div className={`flex h-11 w-11 items-center justify-center rounded-lg ${card.color}`}>
              <card.icon className="h-5 w-5" />
            </div>
            <div>
              <div className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">{card.label}</div>
              <div className="text-xl font-bold text-slate-900 dark:text-slate-100">{card.value}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex rounded-xl bg-slate-100 p-1 dark:bg-slate-800/80">
          <button
            type="button"
            onClick={() => setEligibilityFilter('all')}
            className={`rounded-lg px-4 py-2 text-sm font-bold transition ${eligibilityFilter === 'all' ? 'bg-white text-slate-900 shadow-sm dark:bg-slate-900 dark:text-slate-100' : 'text-slate-500 hover:text-slate-700 dark:text-slate-300 dark:hover:text-slate-100'}`}
          >
            Tümü
          </button>
          <button
            type="button"
            onClick={() => setEligibilityFilter('eligible')}
            className={`rounded-lg px-4 py-2 text-sm font-bold transition ${eligibilityFilter === 'eligible' ? 'bg-white text-emerald-700 shadow-sm dark:bg-slate-900 dark:text-emerald-300' : 'text-slate-500 hover:text-slate-700 dark:text-slate-300 dark:hover:text-slate-100'}`}
          >
            Hakeden
          </button>
          <button
            type="button"
            onClick={() => setEligibilityFilter('ineligible')}
            className={`rounded-lg px-4 py-2 text-sm font-bold transition ${eligibilityFilter === 'ineligible' ? 'bg-white text-rose-700 shadow-sm dark:bg-slate-900 dark:text-rose-300' : 'text-slate-500 hover:text-slate-700 dark:text-slate-300 dark:hover:text-slate-100'}`}
          >
            Hak Etmeyen
          </button>
        </div>
        <div className="relative min-w-[220px] flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Genel arama..."
            className="h-10 w-full rounded-xl border border-slate-300 bg-white pl-9 pr-3 text-sm text-slate-900 outline-none focus:border-blue-500 dark:border-slate-700 dark:bg-[#171717] dark:text-slate-100"
          />
        </div>

        <button
          type="button"
          onClick={() => setShowFilterPanel((current) => !current)}
          className={`inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-sm font-semibold ${showFilterPanel ? 'border-blue-300 bg-blue-50 text-blue-700 dark:border-blue-800 dark:bg-blue-950/40 dark:text-blue-200' : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-[#171717] dark:text-slate-200 dark:hover:bg-[#202020]'}`}
        >
          <Filter className="h-4 w-4" />
          Filtrele
          {activeFilterCount > 0 && <span className="ml-1 rounded-full bg-blue-600 px-1.5 py-0.5 text-[10px] font-bold text-white">{activeFilterCount}</span>}
        </button>

        <button
          type="button"
          onClick={() => setShowColumnPanel((current) => !current)}
          className={`inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-sm font-semibold ${showColumnPanel ? 'border-orange-300 bg-orange-50 text-orange-700 dark:border-orange-800 dark:bg-orange-950/40 dark:text-orange-200' : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-[#171717] dark:text-slate-200 dark:hover:bg-[#202020]'}`}
        >
          {showColumnPanel ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          Sütunlar
        </button>

        <select
          value={pageSize}
          onChange={(e) => setPageSize(Number(e.target.value))}
          className="h-10 rounded-xl border border-slate-300 bg-white px-3 text-sm font-medium text-slate-800 outline-none dark:border-slate-700 dark:bg-[#171717] dark:text-slate-100"
        >
          <option value={25}>25 kayıt</option>
          <option value={50}>50 kayıt</option>
          <option value={100}>100 kayıt</option>
          <option value={250}>250 kayıt</option>
        </select>

        <div className="ml-auto text-sm text-slate-600 dark:text-slate-400">
          {sortedRows.length} kayıt
        </div>
      </div>

      {showFilterPanel && (
        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3 dark:border-slate-800 dark:bg-[#151515]">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-sm font-bold text-slate-700 dark:text-slate-200">
              <Filter className="h-4 w-4" />
              Filtre ve Gruplandırma
            </div>
            <button type="button" onClick={clearFilters} className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-[#171717] dark:text-slate-200">
              <X className="h-4 w-4" />
              Temizle
            </button>
          </div>

          <div className="space-y-2">
            {filters.length === 0 && (
              <div className="rounded-xl border border-dashed border-slate-300 p-4 text-center text-sm text-slate-400 dark:border-slate-700">
                Filtre tanımlı değil. Aşağıdaki buton ile bir filtre kuralı ekleyin.
              </div>
            )}
            {filters.map((rule, index) => (
              <div key={rule.id} className="grid gap-2 lg:grid-cols-[90px_220px_180px_minmax(220px,1fr)_48px] lg:items-center">
                <select
                  value={index === 0 ? 'and' : rule.join}
                  onChange={(e) => updateFilterRule(rule.id, { join: e.target.value as FilterJoin })}
                  disabled={index === 0}
                  className="h-9 rounded-lg border border-slate-300 bg-white px-2 text-sm text-slate-800 outline-none disabled:opacity-60 dark:border-slate-700 dark:bg-[#171717] dark:text-slate-100"
                >
                  <option value="and">Ve</option>
                  <option value="or">Veya</option>
                </select>

                <select
                  value={rule.column}
                  onChange={(e) => {
                    const column = e.target.value as ColumnKey;
                    updateFilterRule(rule.id, {
                      column,
                      operator: DATE_FILTER_COLUMNS.has(column) ? 'dateEquals' : 'contains',
                    });
                  }}
                  className="h-9 rounded-lg border border-slate-300 bg-white px-2 text-sm text-slate-800 outline-none dark:border-slate-700 dark:bg-[#171717] dark:text-slate-100"
                >
                  {REPORT_COLUMNS.map((column) => (
                    <option key={column.key} value={column.key}>{column.label}</option>
                  ))}
                </select>

                <select
                  value={rule.operator}
                  onChange={(e) => updateFilterRule(rule.id, { operator: e.target.value as FilterOperator })}
                  className="h-9 rounded-lg border border-slate-300 bg-white px-2 text-sm text-slate-800 outline-none dark:border-slate-700 dark:bg-[#171717] dark:text-slate-100"
                >
                  <option value="contains">İçinde</option>
                  <option value="notContains">İçermiyor</option>
                  <option value="equals">Eşit</option>
                  <option value="notEquals">Eşit Değil</option>
                  {DATE_FILTER_COLUMNS.has(rule.column) && <option value="dateEquals">Tarih =</option>}
                  {DATE_FILTER_COLUMNS.has(rule.column) && <option value="dateOnOrAfter">Tarih &gt;=</option>}
                  {DATE_FILTER_COLUMNS.has(rule.column) && <option value="dateOnOrBefore">Tarih &lt;=</option>}
                  {DATE_FILTER_COLUMNS.has(rule.column) && <option value="dateRange">Özel Tarih Aralığı</option>}
                  {DATE_FILTER_COLUMNS.has(rule.column) && <option value="dateToday">Bugün</option>}
                  {DATE_FILTER_COLUMNS.has(rule.column) && <option value="dateThisWeek">Bu Hafta</option>}
                  {DATE_FILTER_COLUMNS.has(rule.column) && <option value="dateThisMonth">Bu Ay</option>}
                  {DATE_FILTER_COLUMNS.has(rule.column) && <option value="dateLastMonth">Geçen Ay</option>}
                  <option value="empty">Boş</option>
                  <option value="notEmpty">Dolu</option>
                </select>

                {DATE_FILTER_COLUMNS.has(rule.column) && rule.operator === 'dateRange' ? (
                  <div className="flex items-center gap-1">
                    <input
                      type="date"
                      value={String(rule.value).split(',')[0] || ''}
                      onChange={(e) => updateFilterRule(rule.id, { value: `${e.target.value},${String(rule.value).split(',')[1] || ''}` })}
                      placeholder="Başlangıç"
                      className="h-9 w-full rounded-lg border border-slate-300 bg-white px-2 text-sm text-slate-900 outline-none dark:border-slate-700 dark:bg-[#171717] dark:text-slate-100"
                    />
                    <span className="shrink-0 text-xs text-slate-400">→</span>
                    <input
                      type="date"
                      value={String(rule.value).split(',')[1] || ''}
                      onChange={(e) => updateFilterRule(rule.id, { value: `${String(rule.value).split(',')[0] || ''},${e.target.value}` })}
                      placeholder="Bitiş"
                      className="h-9 w-full rounded-lg border border-slate-300 bg-white px-2 text-sm text-slate-900 outline-none dark:border-slate-700 dark:bg-[#171717] dark:text-slate-100"
                    />
                  </div>
                ) : DATE_FILTER_COLUMNS.has(rule.column) && (rule.operator === 'dateEquals' || rule.operator === 'dateOnOrAfter' || rule.operator === 'dateOnOrBefore') ? (
                  <input
                    type="date"
                    value={rule.value}
                    onChange={(e) => updateFilterRule(rule.id, { value: e.target.value })}
                    className="h-9 w-full rounded-lg border border-slate-300 bg-white px-2 text-sm text-slate-900 outline-none dark:border-slate-700 dark:bg-[#171717] dark:text-slate-100"
                  />
                ) : (
                  <input
                    value={rule.value}
                    onChange={(e) => updateFilterRule(rule.id, { value: e.target.value })}
                    disabled={rule.operator === 'empty' || rule.operator === 'notEmpty' || rule.operator === 'dateToday' || rule.operator === 'dateThisWeek' || rule.operator === 'dateThisMonth' || rule.operator === 'dateLastMonth'}
                    placeholder="Filtre değeri"
                    className="h-9 w-full rounded-lg border border-slate-300 bg-white px-2 text-sm text-slate-900 outline-none disabled:opacity-60 dark:border-slate-700 dark:bg-[#171717] dark:text-slate-100"
                  />
                )}

                <button type="button" onClick={() => removeFilterRule(rule.id)} className="inline-flex h-9 items-center justify-center rounded-lg border border-rose-300 bg-rose-50 text-rose-700 hover:bg-rose-100 dark:border-rose-800 dark:bg-rose-950/40 dark:text-rose-200">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>

          <button type="button" onClick={addFilterRule} className="mt-3 inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-[#171717] dark:text-slate-200">
            <Plus className="h-4 w-4" />
            Yeni Filtre Ekle
          </button>
        </div>
      )}

      {showColumnPanel && (
        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3 dark:border-slate-800 dark:bg-[#151515]">
          <div className="mb-2 flex items-center justify-between gap-2">
            <div className="text-sm font-semibold text-slate-700 dark:text-slate-200">Sütun görünürlüğü</div>
            <button
              type="button"
              onClick={() => setVisibleColumns(DEFAULT_VISIBLE_COLUMNS)}
              className="rounded-lg border border-slate-300 bg-white px-2.5 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-[#171717] dark:text-slate-200"
            >
              Varsayılanları Geri Yükle
            </button>
          </div>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
            {activeColumns.map((column) => {
              const visible = visibleColumns.includes(column.key);
              return (
                <button
                  key={column.key}
                  type="button"
                  onClick={() => {
                    setVisibleColumns((current) => (visible ? current.filter((key) => key !== column.key) : [...current, column.key]));
                  }}
                  className={`flex items-center gap-2 rounded-xl border px-3 py-2 text-left text-sm ${visible ? 'border-orange-200 bg-white text-slate-800 dark:border-orange-900/70 dark:bg-[#111111] dark:text-slate-100' : 'border-slate-200 bg-white/70 text-slate-500 dark:border-slate-800 dark:bg-[#101010] dark:text-slate-500'}`}
                >
                  {visible ? <Eye className="h-4 w-4 shrink-0" /> : <EyeOff className="h-4 w-4 shrink-0" />}
                  <span className="truncate">{column.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {reasonTabs.length > 1 && (
        <div className="flex flex-wrap gap-2">
          {reasonTabs.map((reason) => {
            const isActive = reasonFilter === reason;
            const count = reason === 'all'
              ? report.details.filter((row) => !row.eligible).length
              : report.details.filter((row) => !row.eligible && (row.notes || '-') === reason).length;
            return (
              <button
                key={reason}
                type="button"
                onClick={() => setReasonFilter(reason)}
                className={`rounded-full px-3 py-1.5 text-xs font-semibold transition ${isActive ? 'bg-rose-600 text-white' : 'bg-white text-slate-700 hover:bg-slate-50 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800'}`}
              >
                {reason === 'all' ? 'Tüm Nedenler' : reason} ({count})
              </button>
            );
          })}
        </div>
      )}

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/50 dark:text-red-300">
          {error}
        </div>
      )}

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-[#111111]">
        <div className="overflow-auto" style={{ maxHeight: 'calc(100vh - 320px)', minHeight: '280px' }}>
          <table className="min-w-full border-collapse text-[13px]">
            <thead className="sticky top-0 z-20 bg-slate-100 text-slate-700 dark:bg-[#181818] dark:text-slate-200">
              <tr>
                <th className="sticky left-0 z-30 whitespace-nowrap border-b border-slate-200 bg-slate-100 px-3 py-2.5 text-left font-bold dark:border-slate-800 dark:bg-[#181818]" style={{ minWidth: '52px', width: '52px' }}>
                  <input type="checkbox" checked={allVisibleSelected} onChange={toggleVisibleRows} />
                </th>
                {activeColumns.map((column, columnIndex) => (
                  <th
                    key={column.key}
                    className={`whitespace-nowrap border-b border-slate-200 px-3 py-2.5 font-bold dark:border-slate-800 ${column.align === 'right' ? 'text-right' : 'text-left'} ${columnIndex === 0 ? 'sticky left-[52px] z-30 bg-slate-100 dark:bg-[#181818]' : ''}`}
                    style={{ minWidth: `${column.width}px`, width: `${column.width}px` }}
                  >
                    {renderSortHeader(column.label, column.key, column.align === 'right' ? 'right' : 'left')}
                  </th>
                ))}
                <th className="sticky right-0 z-30 whitespace-nowrap border-b border-slate-200 bg-slate-100 px-3 py-2.5 text-left font-bold dark:border-slate-800 dark:bg-[#181818]" style={{ minWidth: '150px', width: '150px' }}>
                  İşlem
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={activeColumns.length + 2} className="px-4 py-10 text-center text-sm text-slate-400">
                    <Loader2 className="mx-auto h-5 w-5 animate-spin" />
                  </td>
                </tr>
              ) : paginatedRows.length === 0 ? (
                <tr>
                  <td colSpan={activeColumns.length + 2} className="px-4 py-10 text-center text-sm font-medium text-slate-400">Kayıt bulunamadı.</td>
                </tr>
              ) : paginatedRows.map((row, index) => (
                <Fragment key={`${row.shipmentId}-${index}`}>
                  <tr className="group hover:bg-slate-50 dark:hover:bg-slate-900">
                    <td className="sticky left-0 z-10 whitespace-nowrap border-b border-slate-100 bg-white px-3 py-2 group-hover:bg-slate-50 dark:border-slate-800/60 dark:bg-[#111111] dark:group-hover:bg-slate-900">
                      <input type="checkbox" checked={selectedShipmentIds.includes(String(row.shipmentId))} onChange={() => toggleShipmentSelection(String(row.shipmentId))} />
                    </td>
                    {activeColumns.map((column, columnIndex) => (
                      <td
                        key={column.key}
                        className={`whitespace-nowrap border-b border-slate-100 px-3 py-2 text-slate-700 dark:border-slate-800/60 dark:text-slate-200 ${column.align === 'right' ? 'text-right' : 'text-left'} ${columnIndex === 0 ? 'sticky left-[52px] z-10 bg-white group-hover:bg-slate-50 dark:bg-[#111111] dark:group-hover:bg-slate-900' : ''}`}
                        style={{ minWidth: `${column.width}px`, maxWidth: column.width > 220 ? `${column.width + 80}px` : undefined }}
                      >
                        <div className={column.width > 220 ? 'max-w-[280px] truncate' : ''} title={column.width > 220 ? String(row[column.key] ?? row.cityDistrict ?? '-') : undefined}>
                          {renderCell(row, column)}
                        </div>
                      </td>
                    ))}
                    <td className="sticky right-0 z-10 whitespace-nowrap border-b border-slate-100 bg-white px-3 py-2 group-hover:bg-slate-50 dark:border-slate-800/60 dark:bg-[#111111] dark:group-hover:bg-slate-900">
                      {row.transportModel === 'own-fleet' ? (
                        <div className="flex flex-col gap-1">
                        <button
                          type="button"
                          onClick={() => void handleShipmentRecalculate(row.shipmentId)}
                          disabled={recalculatingShipmentId === row.shipmentId || backfillingOwnFleet}
                          className="inline-flex items-center gap-1 rounded-lg border border-amber-300 bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-700 hover:bg-amber-100 disabled:opacity-50 dark:border-amber-800 dark:bg-amber-950/50 dark:text-amber-300"
                        >
                          {recalculatingShipmentId === row.shipmentId ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                          Yeniden Hesapla
                        </button>
                        <button
                          type="button"
                          onClick={() => void handleVerifyShipment(row.shipmentId)}
                          disabled={verifyingShipmentId === row.shipmentId || backfillingOwnFleet}
                          className="inline-flex items-center gap-1 rounded-lg border border-blue-300 bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700 hover:bg-blue-100 disabled:opacity-50 dark:border-blue-800 dark:bg-blue-950/50 dark:text-blue-300"
                        >
                          {verifyingShipmentId === row.shipmentId ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Eye className="h-3.5 w-3.5" />}
                          Doğrula
                        </button>
                        </div>
                      ) : row.transportModel === 'spot' ? (
                        <div className="flex flex-col gap-1">
                          <button
                            type="button"
                            onClick={() => void handleSpotShipmentRecalculate(row.shipmentId)}
                            disabled={recalculatingShipmentId === row.shipmentId || backfillingOwnFleet}
                            className="inline-flex items-center gap-1 rounded-lg border border-amber-300 bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-700 hover:bg-amber-100 disabled:opacity-50 dark:border-amber-800 dark:bg-amber-950/50 dark:text-amber-300"
                          >
                            {recalculatingShipmentId === row.shipmentId ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                            Yeniden Hesapla
                          </button>
                          <button
                            type="button"
                            onClick={() => handleSpotLocationCheck(row)}
                            className="inline-flex items-center gap-1 rounded-lg border border-violet-300 bg-violet-50 px-2.5 py-1 text-xs font-medium text-violet-700 hover:bg-violet-100 dark:border-violet-800 dark:bg-violet-950/50 dark:text-violet-300"
                          >
                            <MapPin className="h-3.5 w-3.5" />
                            Konum Kontrol
                          </button>
                        </div>
                      ) : (
                        <span className="text-xs text-slate-400">-</span>
                      )}
                    </td>
                  </tr>
                  {recalculationDetails[String(row.shipmentId)] ? (
                    <tr className="bg-slate-50/80 dark:bg-slate-900/60">
                      <td colSpan={activeColumns.length + 2} className="px-4 py-3">
                        <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                          Yeniden Hesapla Detayı: Seyir'den {recalculationDetails[String(row.shipmentId)].locationCount} konum bulundu, {recalculationDetails[String(row.shipmentId)].logsWritten} log yazıldı.
                        </div>
                        <div className="mb-3 overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700">
                          <table className="w-full text-left text-xs">
                            <thead className="bg-white dark:bg-slate-950">
                              <tr>
                                <th className="px-3 py-2 font-semibold">Karşılaştırılan Nokta</th>
                                <th className="px-3 py-2 font-semibold">Route Konumu</th>
                                <th className="px-3 py-2 font-semibold">Sipariş Teslim Konumu</th>
                                <th className="px-3 py-2 text-right font-semibold">Route-Sipariş Farkı</th>
                                <th className="px-3 py-2 text-right font-semibold">Geofence</th>
                                <th className="px-3 py-2 font-semibold">En Yakın Seyir Konumu</th>
                                <th className="px-3 py-2 text-right font-semibold">Mesafe</th>
                                <th className="px-3 py-2 text-right font-semibold">İşlem</th>
                              </tr>
                            </thead>
                            <tbody>
                              {recalculationDetails[String(row.shipmentId)].pointDebug.map((point) => (
                                <tr key={`${row.shipmentId}-${point.pointKey}`} className="border-t border-slate-100 dark:border-slate-800">
                                  <td className="px-3 py-2">
                                    <div className="font-medium text-slate-700 dark:text-slate-200">{point.pointName}</div>
                                    <div className="text-[11px] text-slate-500 dark:text-slate-400">{point.pointType} / {point.pointSource || '-'}</div>
                                  </td>
                                  <td className="px-3 py-2 font-mono text-[11px]">
                                    {point.latitude ?? '-'}, {point.longitude ?? '-'}
                                  </td>
                                  <td className="px-3 py-2 font-mono text-[11px]">
                                    {point.shipmentDeliveryLatitude ?? '-'}, {point.shipmentDeliveryLongitude ?? '-'}
                                  </td>
                                  <td className="px-3 py-2 text-right">{point.deliveryCoordinateDiffKm != null ? `${point.deliveryCoordinateDiffKm} km` : '-'}</td>
                                  <td className="px-3 py-2 text-right">
                                    <div>{point.geofenceKm} km</div>
                                    {point.overrideGeofenceKm != null ? (
                                      <div className="text-[11px] text-emerald-600 dark:text-emerald-400">Override</div>
                                    ) : null}
                                  </td>
                                  <td className="px-3 py-2">
                                    {point.nearestLocation ? (
                                      <>
                                        <div>{formatDateTimeTr(point.nearestLocation.dataTime)}</div>
                                        <div className="font-mono text-[11px] text-slate-500 dark:text-slate-400">{point.nearestLocation.latitude ?? '-'}, {point.nearestLocation.longitude ?? '-'}</div>
                                        <div className="text-[11px] text-slate-500 dark:text-slate-400">Plaka: {point.nearestLocation.plate || '-'}</div>
                                      </>
                                    ) : '-'}
                                  </td>
                                  <td className="px-3 py-2 text-right">{point.nearestLocation ? `${point.nearestLocation.distanceKm} km` : '-'}</td>
                                  <td className="px-3 py-2 text-right">
                                    {point.nearestLocation ? (
                                      <button
                                        type="button"
                                        onClick={() => void handleAcceptNearestGeofence(String(row.shipmentId), point)}
                                        disabled={acceptingGeofenceKey === `${row.shipmentId}:${point.pointKey}` || backfillingOwnFleet || recalculatingShipmentId === row.shipmentId}
                                        className="inline-flex items-center gap-1 rounded-lg border border-emerald-300 bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700 hover:bg-emerald-100 disabled:opacity-50 dark:border-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-300"
                                      >
                                        {acceptingGeofenceKey === `${row.shipmentId}:${point.pointKey}` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                                        Kabul Et
                                      </button>
                                    ) : (
                                      <span className="text-slate-400">-</span>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700">
                          <table className="w-full text-left text-xs">
                            <thead className="bg-white dark:bg-slate-950">
                              <tr>
                                <th className="px-3 py-2 font-semibold">Gün</th>
                                <th className="px-3 py-2 font-semibold">Sorgu Aralığı</th>
                                <th className="px-3 py-2 text-right font-semibold">Seyir Konum</th>
                                <th className="px-3 py-2 text-right font-semibold">Arrival</th>
                                <th className="px-3 py-2 text-right font-semibold">Departure</th>
                              </tr>
                            </thead>
                            <tbody>
                              {recalculationDetails[String(row.shipmentId)].dayStats.map((day) => (
                                <tr key={`${row.shipmentId}-${day.date}`} className="border-t border-slate-100 dark:border-slate-800">
                                  <td className="px-3 py-2">{day.date}</td>
                                  <td className="px-3 py-2">{day.startDateTime} - {day.endDateTime}</td>
                                  <td className="px-3 py-2 text-right">{day.locationCount}</td>
                                  <td className="px-3 py-2 text-right">{day.arrivalsWritten}</td>
                                  <td className="px-3 py-2 text-right">{day.departuresWritten}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </td>
                    </tr>
                  ) : null}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex flex-col gap-3 border-t border-slate-200 bg-white px-4 py-3 sm:flex-row sm:items-center sm:justify-between dark:border-slate-800 dark:bg-[#141414]">
          <div className="text-sm text-slate-600 dark:text-slate-400">
            {sortedRows.length === 0
              ? 'Kayıt yok'
              : `${(clampedPage - 1) * pageSize + 1}-${Math.min(clampedPage * pageSize, sortedRows.length)} / ${sortedRows.length} kayıt`}
          </div>
          <div className="flex items-center gap-3">
            <button type="button" onClick={() => setPage(clampedPage - 1)} disabled={clampedPage <= 1} className="inline-flex items-center gap-1 rounded-lg border border-slate-300 px-2.5 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800">
              <ChevronLeft className="h-4 w-4" /> Önceki
            </button>
            <span className="text-sm font-medium text-slate-700 dark:text-slate-200">Sayfa {clampedPage} / {totalPages}</span>
            <button type="button" onClick={() => setPage(clampedPage + 1)} disabled={clampedPage >= totalPages} className="inline-flex items-center gap-1 rounded-lg border border-slate-300 px-2.5 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800">
              Sonraki <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
    {verifyData && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setVerifyData(null)}>
        <div className="flex max-h-[90vh] w-full max-w-6xl flex-col rounded-2xl bg-white shadow-2xl dark:bg-[#111111]" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4 dark:border-slate-800">
            <div>
              <h2 className="text-lg font-bold text-slate-900 dark:text-white">Sevkiyat Doğrulama</h2>
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                {verifyData.shipmentNo || verifyData.shipmentId} &bull; {verifyData.plate || '-'} &bull; {verifyData.driverName || '-'}
              </p>
              <p className="text-xs text-slate-400 dark:text-slate-500">
                Tarih Aralığı: {formatDateTimeTr(verifyData.dateRange?.start)} &ndash; {formatDateTimeTr(verifyData.dateRange?.end)} &bull; Seyir Konum: {verifyData.totalLocationLogs || 0}
              </p>
            </div>
            <button type="button" onClick={() => setVerifyData(null)} className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800">
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-6">
            {!verifyData.points || verifyData.points.length === 0 ? (
              <p className="text-center text-sm text-slate-500 dark:text-slate-400">Nokta bulunamadı</p>
            ) : (
              <div className="space-y-4">
                {verifyData.points.map((point: any) => {
                  const isExpanded = verifyExpandedPoints[point.pointKey] ?? false;
                  const insideLogs = (point.logs || []).filter((l: any) => l.insideGeofence);
                  return (
                    <div key={point.pointKey} className="rounded-xl border border-slate-200 bg-slate-50/50 dark:border-slate-800 dark:bg-slate-900/50">
                      <div onClick={() => setVerifyExpandedPoints((prev) => ({ ...prev, [point.pointKey]: !prev[point.pointKey] }))} className="flex w-full cursor-pointer items-center justify-between px-4 py-3 text-left">
                        <div>
                          <span className={`inline-block rounded px-2 py-0.5 text-[10px] font-bold uppercase ${point.pointType === 'loading' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300' : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300'}`}>
                            {point.pointType === 'loading' ? 'Yükleme' : 'Teslim'}
                          </span>
                          <span className="ml-2 text-sm font-semibold text-slate-800 dark:text-white">{point.pointName}</span>
                          <span className="ml-2 text-xs text-slate-400">({point.latitude?.toFixed(5)}, {point.longitude?.toFixed(5)})</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <button type="button" onClick={(event) => { event.stopPropagation(); openMapLocation(point.latitude, point.longitude, point.pointName); }} disabled={point.latitude == null || point.longitude == null} className="inline-flex items-center gap-1 rounded-lg border border-blue-300 bg-blue-50 px-2 py-1 text-[11px] font-semibold text-blue-700 hover:bg-blue-100 disabled:opacity-50">
                            <MapPin className="h-3 w-3" /> Konuma Git
                          </button>
                          <span className={`text-xs font-medium ${insideLogs.length > 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-400'}`}>
                            {insideLogs.length} geofence içinde
                          </span>
                          <span className="text-xs text-slate-400">
                            En yakın: {point.nearestDistance != null ? `${point.nearestDistance} km` : '-'}
                          </span>
                          <ChevronRight className={`h-4 w-4 text-slate-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                        </div>
                      </div>
                      {isExpanded && (
                        <div className="border-t border-slate-200 px-4 py-3 dark:border-slate-800">
                          <div className="mb-2 flex items-center gap-2">
                            <span className="text-xs font-medium text-slate-600 dark:text-slate-300">Geofence: {point.geofenceKm} km</span>
                            <span className="text-xs text-slate-400">|</span>
                            <span className="text-xs text-slate-400">Toplam konum: {(point.logs || []).length}</span>
                          </div>
                          {point.logs && point.logs.length > 0 ? (
                            <div className="max-h-64 overflow-auto rounded-lg border border-slate-200 dark:border-slate-700">
                              <table className="w-full text-left text-[11px]">
                                <thead className="sticky top-0 bg-white dark:bg-slate-950">
                                  <tr>
                                    <th className="px-2.5 py-1.5 font-semibold">Saat</th>
                                    <th className="px-2.5 py-1.5 font-semibold">Plaka</th>
                                    <th className="px-2.5 py-1.5 text-right font-semibold">Mesafe (km)</th>
                                    <th className="px-2.5 py-1.5 text-center font-semibold">Durum</th>
                                    <th className="px-2.5 py-1.5 text-center font-semibold">Konum</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {point.logs.map((log: any, idx: number) => (
                                    <tr key={idx} className={`border-t border-slate-100 dark:border-slate-800 ${log.insideGeofence ? 'bg-emerald-50/60 dark:bg-emerald-900/20' : ''}`}>
                                      <td className="px-2.5 py-1.5 text-slate-700 dark:text-slate-300">{formatDateTimeTr(log.dataTime)}</td>
                                      <td className="px-2.5 py-1.5 text-slate-600 dark:text-slate-400">{log.plate || '-'}</td>
                                      <td className="px-2.5 py-1.5 text-right font-medium">{log.distanceKm != null ? `${log.distanceKm}` : '-'}</td>
                                      <td className="px-2.5 py-1.5 text-center">
                                        {log.insideGeofence
                                          ? <span className="inline-flex items-center gap-0.5 text-emerald-600 dark:text-emerald-400"><BadgeCheck className="h-3 w-3" /> İçeride</span>
                                          : <span className="text-slate-400">Dışarıda</span>
                                        }
                                      </td>
                                      <td className="px-2.5 py-1.5 text-center">
                                        <button type="button" onClick={() => openMapLocation(log.latitude, log.longitude, `${log.plate || 'Seyir'} ${formatDateTimeTr(log.dataTime)}`)} disabled={log.latitude == null || log.longitude == null} className="inline-flex items-center gap-1 rounded-lg border border-blue-300 bg-blue-50 px-2 py-1 text-[11px] font-semibold text-blue-700 hover:bg-blue-100 disabled:opacity-50">
                                          <MapPin className="h-3 w-3" /> Git
                                        </button>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          ) : (
                            <p className="text-xs text-slate-400">Bu nokta için konum kaydı yok</p>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    )}
    </>
  );
}
